import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { formatApiErrorDetail } from "@/lib/api";
import { ADMIN_PATH } from "@/lib/routes";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [needTotp, setNeedTotp] = useState(false);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const { login } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const extra = needTotp
        ? (useRecovery ? { recovery_code: recoveryCode } : { totp_code: totpCode })
        : {};
      const user = await login(email, password, extra);
      toast.success(`Welcome back, ${user.name}`);
      nav(user.role === "admin" ? ADMIN_PATH : "/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "totp_required") {
        setNeedTotp(true);
        toast.info("Enter the 6-digit code from your authenticator app");
      } else if (detail === "totp_invalid") {
        if (useRecovery) setRecoveryCode(""); else setTotpCode("");
        toast.error(useRecovery ? "Recovery code invalid or already used" : "Incorrect code — try the next one shown in your app");
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally {
      setBusy(false);
    }
  };

  const submitDisabled = busy || (
    needTotp && (useRecovery ? recoveryCode.replace(/\s|-/g, "").length < 10 : totpCode.length !== 6)
  );

  return (
    <div className="container-bl py-14 max-w-md" data-testid="page-login">
      <Seo title="Sign in · B&L Rentals" description="Sign in to manage your rental bookings and referral code." />
      <h1 className="text-3xl font-heading font-bold text-white">Sign in</h1>
      <p className="mt-1 text-sm text-[#AEB9C9]">Access bookings, reviews, and your referral code.</p>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <Label>Email</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} disabled={needTotp} data-testid="login-email" />
        </div>
        <div>
          <Label>Password</Label>
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} disabled={needTotp} data-testid="login-password" />
        </div>
        {needTotp && !useRecovery && (
          <div data-testid="login-totp-block">
            <Label>Authenticator code</Label>
            <Input
              type="text" inputMode="numeric" autoComplete="one-time-code" autoFocus required maxLength={6}
              placeholder="6-digit code"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              data-testid="login-totp-code"
            />
            <p className="mt-1 text-xs text-[#AEB9C9]">Open Google Authenticator / 1Password / Authy and enter the current 6-digit code.</p>
            <button
              type="button"
              onClick={() => { setUseRecovery(true); setTotpCode(""); }}
              className="mt-2 text-xs text-[#E2AB20] hover:underline"
              data-testid="login-use-recovery"
            >Lost your device? Use a recovery code instead →</button>
          </div>
        )}
        {needTotp && useRecovery && (
          <div data-testid="login-recovery-block">
            <Label>Recovery code</Label>
            <Input
              type="text" autoFocus required
              placeholder="XXXXX-XXXXX"
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value.toUpperCase().slice(0, 12))}
              data-testid="login-recovery-code"
            />
            <p className="mt-1 text-xs text-[#AEB9C9]">Enter one of the 8 one-time codes you saved when setting up 2FA. Each code works once.</p>
            <button
              type="button"
              onClick={() => { setUseRecovery(false); setRecoveryCode(""); }}
              className="mt-2 text-xs text-[#E2AB20] hover:underline"
              data-testid="login-use-totp"
            >← Back to authenticator code</button>
          </div>
        )}
        <Button type="submit" disabled={submitDisabled} className="w-full bg-[#E2AB20] hover:bg-[#D89B08] text-[#010F27] font-semibold h-11" data-testid="login-submit">
          {busy ? "Signing in…" : needTotp ? "Verify & sign in" : "Sign in"}
        </Button>
        {needTotp && (
          <button
            type="button"
            onClick={() => { setNeedTotp(false); setUseRecovery(false); setTotpCode(""); setRecoveryCode(""); setPassword(""); }}
            className="w-full text-xs text-[#AEB9C9] hover:text-white"
            data-testid="login-totp-cancel"
          >← Start over</button>
        )}
      </form>

      <div className="my-6 flex items-center gap-4 text-xs text-[#8896AC]">
        <div className="flex-1 h-px bg-gray-200" /> OR <div className="flex-1 h-px bg-gray-200" />
      </div>

      <Button type="button" variant="outline" onClick={googleLogin} className="w-full h-11" data-testid="google-login">
        Continue with Google
      </Button>

      <p className="mt-6 text-sm text-[#AEB9C9] text-center">
        New here? <Link to="/register" className="link-blue" data-testid="link-register">Create an account</Link>
      </p>
    </div>
  );
}
